import React, {FC, RefObject, useContext, useRef} from 'react';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {PushContext} from '../../../../../srcCommon/push/pushContext';
import {TeammateLicenseTypesEnum} from '../../../../models/api/teammateModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {VisualWorkspaceTeamData} from '../../../../models/api/visualWorkspaceModel';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {TeamWorkspaceInfoBubble} from '../../../infoBubbles/teamWorkspaceInfoBubble';
import {WorkspaceHeader} from '../header/workspaceHeader';
import {Workspace} from '../workspace';
import {TeamWorkspaceCustomViewsContainer} from './teamWorkspaceCustomViewsContainer';
import {TeamWorkspaceInboxesContainer} from './teamWorkspaceInboxesContainer';

/*
 * Props.
 */

export interface TeamWorkspaceProps {
  panelId: InboxesPanelId;
  /** The team that this workspace is for. */
  team: TeamModel;
  /** Teams data passed from VisualWorkspaceModel */
  visualWorkspaceTeam: VisualWorkspaceTeamData;
  /** User license type (full). */
  userLicenseType: TeammateLicenseTypesEnum;
  /** Whether the user has permission to add an inbox to this team workspace. */
  hasAddInboxPermission: boolean;
  /** Whether this workspace is collapsed or not. */
  isExpanded: boolean;
  /** Whether this is the first team. */
  isFirstTeam: boolean;
  /** Event handler to expand/collapse the workspace. */
  onToggleExpandClick: Handler;
}

/*
 * Component.
 */

/** Display a workspace for the specified team. */
export const TeamWorkspace: FC<TeamWorkspaceProps> = (props) => {
  const {isConnected} = useContext(PushContext);
  const {panelId, team, isExpanded, isFirstTeam, userLicenseType} = props;

  const teamWorkspaceRef = useRef<HTMLDivElement>(null);

  if (userLicenseType !== TeammateLicenseTypesEnum.FULL) {
    return null;
  }

  const shouldShowOfflineUI = !isConnected;

  /*
   * Event handlers.
   */

  const onToggleExpandClick = () => {
    props.onToggleExpandClick();
  };

  /*
   * Render.
   */

  // Render the content of the workspace.
  const renderContent = () => (
    <>
      {/* Header. */}
      <WorkspaceHeader
        panelId={panelId}
        interactionId="team"
        isExpanded={isExpanded}
        onToggleExpandClick={onToggleExpandClick}
        isOffline={shouldShowOfflineUI}
      >
        {team.name}
      </WorkspaceHeader>

      {/* Info bubble. */}
      {maybeRenderInfoBubble(isFirstTeam, teamWorkspaceRef)}

      {/* Workspace content. */}
      {maybeRenderExpandedContent(props)}
    </>
  );

  // Wrap in a workspace and return.
  return (
    <Workspace ref={isFirstTeam ? teamWorkspaceRef : undefined} isExpanded={isExpanded}>
      {renderContent()}
    </Workspace>
  );
};

function maybeRenderExpandedContent(props: TeamWorkspaceProps) {
  const {panelId, isExpanded, team, visualWorkspaceTeam} = props;
  if (!isExpanded) {
    return null;
  }

  return (
    <>
      <TeamWorkspaceInboxesContainer
        panelId={panelId}
        team={team}
        visualWorkspaceInboxesForTeam={visualWorkspaceTeam.inboxes}
      />
      <TeamWorkspaceCustomViewsContainer
        panelId={panelId}
        team={team}
        visualWorkspaceCustomViewsForTeam={visualWorkspaceTeam.customViews}
      />
    </>
  );
}

function maybeRenderInfoBubble(isFirstTeam: boolean, workspaceRef: RefObject<HTMLElement>) {
  if (!isFirstTeam) {
    return null;
  }

  return <TeamWorkspaceInfoBubble targetRef={workspaceRef} />;
}
